import { useEffect, useState, ReactNode } from 'react';
import { UseFormReturn, FieldValues, SubmitHandler } from 'react-hook-form';
import UserAutocompletion from '../../common/UserAutocompletion';
import InputField from '../InputField/InputField';
import { Form } from 'antd';
import { Button } from '../../Button';
import { BaseRecord } from '../../../interfaces/BaseRecord';
import Alert from 'antd/es/alert/Alert';

interface FieldConfig {
  key: string;
  label?: string;
  type?: string;
  tag?: string;
  defaultValue?: any;
  options?: any[];
  decoratorWord?: string;
  isRequired?: boolean;
  hidden?: boolean;
  warning?: string;
  warningPosition?: 'left' | 'center' | 'right';
  disabled?: boolean;
  tooltip?: string;
  alert?: {
    message: string;
    type: 'success' | 'info' | 'warning' | 'error';
    icon?: React.ReactNode;
  };
}

interface BaseFormProps<T extends FieldValues> {
  formInstance: UseFormReturn<T>;
  fields: FieldConfig[];
  currentRecord?: BaseRecord | null | undefined;
  onSubmit?: SubmitHandler<any>;
  canDelete?: boolean;
  onDelete?: () => void;
  resetForm: () => void;
}

const BaseForm = <T extends FieldValues>({
  formInstance,
  fields,
  currentRecord,
  onSubmit,
  canDelete = false,
  onDelete,
  resetForm,
}: BaseFormProps<T>) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = formInstance;

  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = () => setShowConfirm(true);

  useEffect(() => {
    setShowConfirm(false);
  }, [currentRecord]);

  const renderAlert = (field: FieldConfig) => {
    if (!field.alert) return null;

    const { message, type, icon } = field.alert;

    const alertContent = icon ? (
      <div className="flex items-center gap-2.5">
        <div className="flex items-center justify-center">{icon}</div>
        <p
          className="leading-5"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </div>
    ) : (
      <p className="leading-5" dangerouslySetInnerHTML={{ __html: message }} />
    );

    return (
      <div className="mt-3.5 mb-2.5">
        <Alert message={alertContent} type={type} />
      </div>
    );
  };

  const renderFields: ReactNode = fields?.map((field) => {
    if (field.hidden) return null;
    if (field.type === 'autocomplete') {
      return (
        <div key={field.key}>
          <div className="font-semibold text-primary-100 text-[14px] my-2.5">
            Contact Information
          </div>
          <UserAutocompletion
            currentData={currentRecord}
            watch={watch}
            reset={resetForm}
            register={register}
            control={control}
            errors={errors}
            editable
          />
        </div>
      );
    }
    return (
      <div key={field.key} className="flex flex-col">
        <InputField
          {...field}
          name={field.key ?? ''}
          selectOptions={field.options}
          decorator={field.decoratorWord}
          rules={{ required: !!field.isRequired }}
          control={control}
          errors={errors}
          spaceBetween
          disabled={field.disabled}
          tooltip={field.tooltip}
        />
        {renderAlert(field)}
      </div>
    );
  });

  return (
    <Form
      layout="horizontal"
      onFinish={onSubmit ? handleSubmit(onSubmit) : () => {}}
    >
      <div className="pb-10 max-h-[380px] overflow-y-auto px-4">
        {renderFields}
      </div>
      <div
        className={`bg-white w-full h-16 max-w-[540px] flex gap-1 items-center absolute bottom-0 left-[44%] transform -translate-x-[44%] ${
          currentRecord && canDelete ? 'justify-between' : 'justify-end'
        }`}
      >
        {currentRecord && canDelete && !showConfirm && (
          <Button type="link" htmlType="button" onClick={handleDelete}>
            Delete Entry
          </Button>
        )}
        {currentRecord && canDelete && showConfirm && (
          <div className="flex items-center gap-3 max-w-100">
            <div>
              <p className="font-medium">
                Are you sure you want to delete this entry?
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="link"
                  htmlType="button"
                  className="text-primary-100"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </Button>
                <Button type="link" htmlType="button" onClick={onDelete}>
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        )}
        <Form.Item style={{ margin: 0, padding: 0 }}>
          <Button
            htmlType="submit"
            type="primary"
            className="w-[188px] text-sm"
          >
            Save
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default BaseForm;
